import { SimpleCarousel } from '@eventbrite/collection-carousel';
import { FormattedEvent } from '@eventbrite/event-renderer';
import { usePlacementEventContextAggregator } from '@eventbrite/event-tracker';
import { GenericLazyString, gettext } from '@eventbrite/i18n';
import { trackAnalyticsEvent } from '@eventbrite/site-analytics';
import React from 'react';
import { OrganicEventSearchFiltersApiPayload } from '../../../../api/searchOrganicEvents';
import { BrowseEventCardStyleSwitcher } from '../../../../containers/BrowseEventCardStyleSwitcher';
import { CategoryBrowseBucket } from '../../components/CategoryBrowseBucket';
import { CategoryBrowseEmptyState } from '../../components/CategoryBrowseEmptyState';
import { EventLoadingState } from '../../components/CategoryBrowseEventsLoading';
import { useEnvContext } from '../../context';
import { useBaseEventsQueryEvents } from '../../hooks/queries/events';
import { useLocation } from '../../hooks/queries/location';
import { aggregateContext } from '../../utils/analytics';
import {
    CategoryBrowsePromotedEvent,
    usePromotedEvent,
} from '../CategoryBrowsePromotedEvent';
import { CategoryBrowseViewMore } from '../CategoryBrowseViewMore';
import './CategoryBrowseEvents.scss';

export const VIEW_EVENT_GA_ACTION = 'ViewEvent';
const DEFAULT_TITLE = gettext('Most popular events');

const EventCardSlide: React.FunctionComponent = (props) => {
    return (
        <div className="event-card-simple-carousel-slide">{props.children}</div>
    );
};

interface CategoryBrowseEventsProps {
    queryOverload?: OrganicEventSearchFiltersApiPayload;
    renderLoading?: boolean;
    renderEmptyState?: boolean;
    refetchOnMount?: boolean;
    lazyLoadCarouselElements?: boolean;
    title?: string | GenericLazyString;
    forceRenderHeader?: boolean;
    bucketKey: string;
    bucketPosition?: string;
    optOutDedupeEvents?: boolean;
    includePromotedEvents?: boolean;
}

export const CategoryBrowseEvents = (props: CategoryBrowseEventsProps) => {
    //Get context data
    const { user, env, request } = useEnvContext();
    const domElement = React.useRef(null);

    const isAuthenticated = !!user?.isAuthenticated;
    const locale = env?.localeInfo.locale || 'en_US';
    const bucketTitle = props.title || DEFAULT_TITLE;

    //Fetch necessary data
    const { isLoading: isLocationLoading } = useLocation();
    const { data, isLoading } = useBaseEventsQueryEvents({
        queryOverload: props.queryOverload,
        //For the base bucket we want to ensure it is
        //enabled immediately and refetches on mount
        refetchOnMount: !props.queryOverload,
        optOutDedupeEvents: !props.queryOverload,
    });

    const events = data?.events || [];
    const currentBucketHasContent = events && events.length > 1;

    const { promotedEvents = [], arePromotedEventsLoading } = usePromotedEvent({
        search: props.queryOverload,
        enabled:
            !isLoading &&
            currentBucketHasContent &&
            !!props.includePromotedEvents,
    });
    const promotedEvent = promotedEvents?.[0];

    const handleOnClick = React.useCallback(() => {
        trackAnalyticsEvent({
            category: 'category-page',
            action: VIEW_EVENT_GA_ACTION,
            label: bucketTitle.toString(),
        });
    }, [bucketTitle]);

    usePlacementEventContextAggregator(domElement, ({ id, event }) =>
        aggregateContext({
            id,
            event,
            bucketKey: props.bucketKey,
            bucketPosition: props.bucketPosition,
            events: [...(promotedEvents || []), ...events],
            eventSearch: data?.eventSearch,
            userId: user?.publicId,
            locale,
            sessionId: request?.session_id,
            guestId: user?.guestId,
        }),
    );

    //Loading state
    const loading = isLocationLoading || isLoading;
    if (!props.forceRenderHeader && loading) {
        return <EventLoadingState />;
    }

    if (!props.forceRenderHeader && !currentBucketHasContent) {
        //Only one bucket has `renderLoading` set to true
        if (!props.renderEmptyState) return null;
        return <CategoryBrowseEmptyState />;
    }

    const noPromotedEventFound = !promotedEvent && !arePromotedEventsLoading;

    // Return event bucket
    return (
        <div
            ref={domElement}
            //@TODO: Create a format util to parse human readable label from tabKey and bucketKey
            data-event-bucket-label={props.bucketKey?.toString()}
        >
            <CategoryBrowseBucket>
                <SimpleCarousel
                    slidesVisible={{ desktop: 4, tablet: 2 }}
                    headerTitle={
                        <CategoryBrowseViewMore
                            title={bucketTitle}
                            queryOverload={props.queryOverload}
                        >
                            <h2 className="eds-text-hs">{bucketTitle}</h2>
                        </CategoryBrowseViewMore>
                    }
                    lazilyRenderOffScreenElements={
                        props.lazyLoadCarouselElements
                    }
                >
                    {props.includePromotedEvents && !noPromotedEventFound && (
                        <EventCardSlide>
                            <CategoryBrowsePromotedEvent
                                isLoading={arePromotedEventsLoading}
                                event={promotedEvent}
                                onClick={handleOnClick}
                                data-testid="category-browse-promoted-event"
                            />
                        </EventCardSlide>
                    )}
                    {events?.map((event) => {
                        return (
                            <EventCardSlide key={event.id}>
                                <BrowseEventCardStyleSwitcher
                                    onClick={handleOnClick}
                                    locale={env?.localeInfo.locale || 'en_US'}
                                    statsigLocationString="CategoryBrowse - All"
                                    isAuthenticated={isAuthenticated}
                                    event={event as FormattedEvent}
                                    isLazyImage
                                    affCode={'ebdssbcategorybrowse'}
                                    data-testid="category-browse-organic-event"
                                />
                            </EventCardSlide>
                        );
                    })}
                </SimpleCarousel>
            </CategoryBrowseBucket>
        </div>
    );
};
